import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  Grid,
  IconButton,
  OutlinedInput,
  Popper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import ClearIcon from 'common/shared-ui/src/icons/ClearIcon';

import {
  getIssueProjects,
  getProjectPermissions,
  getQueryRoles,
} from '../../../packages/common/api';
import CheckboxIcon from '../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import CheckedRadioIcon from '../../../packages/common/shared-ui/src/icons/CheckedRadioIcon';
import UncheckedRadioIcon from '../../../packages/common/shared-ui/src/icons/UncheckedRadioIcon';
import { useTranslation } from '../../../packages/common/utils/getLocalString';

// Original class: modalRoot
const StyledModalRoot = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root.MuiDialog-paper': {
    overflow: 'visible',
    padding: '16px',
    maxWidth: '448px',
    backgroundColor: '#fff',
    borderRadius: '16px',
  },

  modalPaper: {
    padding: theme.spacing(2.5, 5),
    overflowY: 'auto',
  },
}));
// Original class: filterPopoverAutocomplete
const StyledFilterPopoverAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '8px 16px',
    paddingRight: '16px',
    lineHeight: '20px',
    height: 'auto',
    borderRadius: theme.spacing(4, 4, 0, 0),
    '& .MuiChip-root': {
      display: 'none',
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#7174AC',
      padding: 0,
      height: 'auto',
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
  },

  '& + .MuiAutocomplete-popper': {
    position: 'static!important',
    transform: 'none!important',
    minWidth: 'auto!important',
    //width: '100%!important',
    maxWidth: '300px!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      border: '1px solid #E4E4EF',
      borderRadius: theme.spacing(0, 0, 4, 4),
      //border: 'none',
      boxShadow: 'none',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          padding: '8px 16px',
          '& .MuiButtonBase-root.MuiCheckbox-root': {
            padding: '2px',
            marginRight: '10px',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
const GridSaveQueryModal = ({
  open,
  handleSave,
  handleClose,
  queryData,
  permission,
  saveLoader,
  state,
}) => {
  const getLocalString = useTranslation();
  const [queryModalError, setQueryModalError] = useState(null);
  const location = useLocation();
  const pageUseParams = useParams();
  const isUserAdmin = useSelector(state => state.user.info.isAdmin);
  const [canSaveGlobalQueries, setCanSaveGlobalQueries] = useState({
    canSaveAgileGlobalQueries: false,
    canSaveConfirmationGlobalQueries: false,
    canSaveIssuesGlobalQueries: false,
    canSaveProjectsGlobalQueries: false,
    canSaveTimesheetsGlobalQueries: false,
  });
  const [modalData, setModalData] = useState({
    name: null,
    accessLevel: 0,
    roles: [],
    projectLevel: pageUseParams.projectId ? 0 : 2,
    projectId: pageUseParams.projectId ? pageUseParams.projectId : null,
  });
  const [initialModalData, setInitialModalData] = useState(null);
  const [roles, setRoles] = useState([]);
  const navigate = useNavigate();
  const handleIssueError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );
  useEffect(() => {
    if (isUserAdmin) {
      setCanSaveGlobalQueries({
        canSaveAgileGlobalQueries: true,
        canSaveConfirmationGlobalQueries: true,
        canSaveIssuesGlobalQueries: true,
        canSaveProjectsGlobalQueries: true,
        canSaveTimesheetsGlobalQueries: true,
      });
    }
  }, [isUserAdmin]);
  useEffect(() => {
    if (queryData) {
      const data = {
        name: queryData.name,
        accessLevel: queryData.accessLevel,
        roles: queryData.roles.map(item => item.roleId),
        projectLevel: queryData.projectLevel,
        projectId: queryData.projectId,
      };
      setInitialModalData(data);
      if (state === 'edit') {
        setModalData({
          name: queryData.name,
          accessLevel: queryData.accessLevel,
          roles: queryData.roles.map(item => item.roleId),
          projectLevel: queryData.projectLevel,
          projectId: queryData.projectId,
        });
      }
    }
  }, [queryData, state]);
  useEffect(() => {
    let ignore = false;
    async function getPermissions() {
      const response = await getProjectPermissions(pageUseParams.projectId);
      if (!ignore) {
        return response;
      }
    }
    if (pageUseParams.projectId) {
      getPermissions()
        .then(res =>
          setCanSaveGlobalQueries({
            canSaveAgileGlobalQueries: res.canSaveAgileGlobalQueries,
            canSaveConfirmationGlobalQueries:
              res.canSaveConfirmationGlobalQueries,
            canSaveIssuesGlobalQueries: res.canSaveIssuesGlobalQueries,
            canSaveProjectsGlobalQueries: res.canSaveProjectsGlobalQueries,
            canSaveTimesheetsGlobalQueries: res.canSaveTimesheetsGlobalQueries,
          }),
        )
        .catch(error => handleIssueError(error.response));
      return () => {
        ignore = true;
      };
    }
  }, [handleIssueError, pageUseParams.projectId]);

  useEffect(() => {
    if (modalData.accessLevel !== 1) {
      return;
    }
    let isMounted = true;
    const getRoles = async () => {
      try {
        const { data: response } = await getQueryRoles();
        if (isMounted) {
          setRoles(response);
        }
      } catch (error) {
        if (isMounted) {
          handleIssueError(error.response ?? error);
        }
      }
    };
    getRoles();
    return () => {
      isMounted = false;
    };
  }, [handleIssueError, modalData?.accessLevel]);

  const handleField = useCallback(
    (data, field) => {
      let value = data;
      if (data.target && data.target.hasOwnProperty('checked')) {
        data.target.checked && (value = 2);
        !data.target.checked && (value = 0);
      }
      setModalData({ ...modalData, [field]: value });
    },
    [modalData],
  );

  const handleSaveButton = useCallback(() => {
    let data = modalData;
    if (modalData) {
      if (!modalData.name || modalData.name === '') {
        setQueryModalError(getLocalString('errors', 'emptyQueryName'));
        return;
      }
      modalData.projectLevel === 0 &&
        pageUseParams.projectId &&
        (data.projectId = pageUseParams.projectId);
      handleSave(data);
    }
  }, [getLocalString, handleSave, modalData, pageUseParams.projectId]);

  const handleCloseButton = useCallback(() => {
    if (state === 'edit') {
      setModalData(initialModalData);
    } else {
      setModalData({
        name: null,
        accessLevel: 0,
        roles: [],
        projectLevel: pageUseParams.projectId ? 0 : 2,
        projectId: null,
      });
    }
    handleClose();
  }, [handleClose, initialModalData, state, pageUseParams.projectId]);

  return (
    <StyledModalRoot open={open} disableRestoreFocus fullWidth={true}>
      {saveLoader && (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            zIndex: 99,
            position: 'absolute',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      )}
      <Grid
        container
        wrap='nowrap'
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <Typography
          variant='h4'
          sx={{
            fontWeight: 600,
            paddingLeft: '8px',
          }}
        >
          {getLocalString(
            'SaveQueryPopup',
            state === 'edit' ? 'editTitle' : 'createTitle',
          )}
        </Typography>
        <IconButton
          disableRipple
          value='close'
          padding={'0'}
          onClick={handleCloseButton}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
            }}
          >
            <ClearIcon width={24} height={24} viewBox='0 0 24 24' />
          </Box>
        </IconButton>
      </Grid>
      {typeof queryModalError === 'string' && (
        <Typography
          variant='h5'
          sx={{
            paddingLeft: '24px',
            fontWeight: 600,
            color: '#E03737',
          }}
        >
          {queryModalError}
        </Typography>
      )}
      <Grid
        container
        sx={{
          padding: '0 8px 0 24px',
          marginBottom: '24px',
          alignItems: 'center',
        }}
      >
        <Grid size={5}>
          <Typography variant='h5'>
            {getLocalString('SaveQueryPopup', 'labels', 'name')}
          </Typography>
        </Grid>
        <Grid
          sx={{
            paddingRight: 4,
          }}
          size={7}
        >
          <OutlinedInput
            fullWidth
            onChange={event => handleField(event.target.value, 'name')}
            placeholder={getLocalString('labels', 'input')}
            value={modalData.name ? modalData.name : ''}
          />
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          padding: '0 8px 0 24px',
          justifyContent: 'flex-end',
        }}
      >
        {canSaveGlobalQueries[permission] && (
          <>
            <Grid
              sx={{
                alignItems: 'flex-start',
              }}
              size={5}
            >
              <Typography variant='h5'>
                {getLocalString('SaveQueryPopup', 'labels', 'visible')}
              </Typography>
            </Grid>
            <Grid
              sx={{
                paddingRight: 4,
              }}
              size={7}
            >
              <RadioGroup
                value={modalData.accessLevel}
                onChange={event =>
                  handleField(Number(event.target.value), 'accessLevel')
                }
              >
                <FormControlLabel
                  value={0}
                  control={
                    <Radio
                      disableRipple
                      icon={
                        <UncheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                      checkedIcon={
                        <CheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                    />
                  }
                  label={getLocalString('SaveQueryPopup', 'labels', 'onlyMe')}
                />
                <FormControlLabel
                  value={2}
                  control={
                    <Radio
                      disableRipple
                      icon={
                        <UncheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                      checkedIcon={
                        <CheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                    />
                  }
                  label={getLocalString('SaveQueryPopup', 'labels', 'all')}
                />
                <FormControlLabel
                  value={1}
                  control={
                    <Radio
                      disableRipple
                      icon={
                        <UncheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                      checkedIcon={
                        <CheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                    />
                  }
                  label={getLocalString('SaveQueryPopup', 'labels', 'roles')}
                />
              </RadioGroup>
              {Number(modalData.accessLevel) === 1 && (
                <Grid
                  container
                  sx={{
                    width: '100%',
                    marginTop: 2,
                  }}
                >
                  <StyledFilterPopoverAutocomplete
                    onChange={(event, newValue) =>
                      handleField(
                        newValue.map(item => {
                          if (item.valueId) {
                            return Number(item.valueId);
                          } else {
                            return item;
                          }
                        }),
                        'roles',
                      )
                    }
                    open={true}
                    multiple
                    loading={roles?.length === 0}
                    disablePortal
                    options={roles}
                    value={modalData.roles}
                    isOptionEqualToValue={(option, value) =>
                      option.valueId == value
                    }
                    disableClearable
                    renderOption={(props, option, { selected }) => {
                      return (
                        <li {...props} key={option.valueId}>
                          <Checkbox
                            icon=<CheckboxIcon
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                            checkedIcon=<CheckboxIconChecked
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.valueName}
                        </li>
                      );
                    }}
                    getOptionLabel={option => option.valueName || ''}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={getLocalString('labels', 'input')}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            endAdornment:
                              roles.length === 0 ? (
                                <CircularProgress color='inherit' size={20} />
                              ) : null,
                          },
                        }}
                      />
                    )}
                    slots={{
                      popper: props => (
                        <Popper {...props} placement='bottom-start'></Popper>
                      ),
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </>
        )}
        <Grid
          sx={{
            paddingRight: 3,
          }}
          size={12}
          offset={5}
        >
          <FormControlLabel
            disabled={!isUserAdmin}
            checked={modalData.projectLevel === 2}
            onChange={event => handleField(event, 'projectLevel')}
            control={
              <Checkbox
                disableRipple
                icon={
                  <CheckboxIcon width='12' height='12' viewBox='0 0 12 12' />
                }
                checkedIcon={
                  <CheckboxIconChecked
                    color={!isUserAdmin ? '#C7CAE6' : '#3448FF'}
                    sx={{ color: !isUserAdmin ? '#C7CAE6' : '#3448FF' }}
                    width='12'
                    height='12'
                    viewBox='0 0 12 12'
                  />
                }
              />
            }
            label={
              <Typography
                variant='h5'
                sx={{ color: !isUserAdmin ? '#C7CAE6' : '#212346' }}
              >
                {getLocalString('SaveQueryPopup', 'labels', 'projects')}
              </Typography>
            }
          />
        </Grid>
        <Grid
          container
          wrap='nowrap'
          sx={{
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '32px',
          }}
        >
          <Box>
            <Button
              disableRipple
              className={clsx('defaultButton', 'primary')}
              onClick={handleSaveButton}
            >
              <Typography
                variant='h4'
                sx={{
                  fontWeight: 700,
                }}
              >
                {getLocalString('buttons', 'save')}
              </Typography>
            </Button>
          </Box>
        </Grid>
      </Grid>
    </StyledModalRoot>
  );
};

export default GridSaveQueryModal;
